<template>
  <div class="text-editor-wrapper transcript-editor">
    <editor-content
      tabindex="0"
      :aria-multiline="true"
      aria-label="Transcript editor"
      role="textbox"
      :editor="transcriptEditor"
      class="text-editor"
      :style="{
        color: fontColour,
        fontWeight: fontBold ? 'bold' : 'normal',
        fontFamily: fontFamily,
        fontSize: fontSize + 'px',
        lineHeight: lineHeight,
        letterSpacing: letterSpacing + 'px',
      }"
    />
    <div class="gradient" :class="isScrolledToBottom ? 'hide' : ''" />
  </div>
</template>

<script>
import { Editor, EditorContent } from "@tiptap/vue-3";
import { Extension, Node, Mark } from "@tiptap/core";
import StarterKit from "@tiptap/starter-kit";
import Typography from "@tiptap/extension-typography";
import Underline from "@tiptap/extension-underline";
import Highlight from "@tiptap/extension-highlight";
import { Color } from "@tiptap/extension-color";
import TextStyle from "@tiptap/extension-text-style";
import Subscript from "@tiptap/extension-subscript";
import Superscript from "@tiptap/extension-superscript";
import Paragraph from "@tiptap/extension-paragraph";
import TextAlign from "@tiptap/extension-text-align";
import Ai, { getHTMLContentBetween } from "@tiptap-pro/extension-ai";
import { dbConstants } from "../../assets/constants";

export default {
  components: {
    EditorContent,
  },

  props: {
    id: String,
    setNumServiceProviders: Function,
    setNumStudents: Function,
    setNumOthers: Function,
    username: String,
    savedTranscript: String,
    handleKeypress: Function,
    transcriptEditor: Object,
    setTranscriptEditor: Function,
    fontColour: String,
    fontBold: Boolean,
    fontFamily: String,
    fontSize: Number,
    lineHeight: Number,
    letterSpacing: Number,
    tiptapToken: String,
  },

  data() {
    return {
      dbConstants,
      isScrolledToBottom: true,
    };
  },

  // computed: {
  //   editor() {
  //     return this.$store.state.user.transcriptEditor;
  //   },
  // },

  mounted() {
    const aiExtension = Ai.extend({
      addCommands() {
        return {
          ...this.parent?.(),
          aiCustomSimplify:
            (options = {}) =>
            ({ editor }) => {
              const { from, to } = options.position;
              const targetText = getHTMLContentBetween(editor, from, to);
              const insertAt = { from, to };

              // Remove HTML tags from the text
              let plainTargetText = '';

              try {
                plainTargetText = new DOMParser().parseFromString(targetText, 'text/html').body.textContent || '';
              } catch (error) {
                plainTargetText = targetText;
                console.error('Error removing HTML tags:', error);
              }

              // Check if no text is bookmarked
              if (plainTargetText.trim().length === 0) {
                return false;
              }

              return editor.commands.aiTextPrompt({
                text: `Simplify the following text: ${plainTargetText}`,
                insertAt,
                ...options,
              })
            },
          aiCustomRephrase:
            (options = {}) =>
            ({ editor }) => {
              const { from, to } = options.position;
              const targetText = getHTMLContentBetween(editor, from, to);
              const insertAt = { from, to };

              // Remove HTML tags from the text
              let plainTargetText = '';

              try {
                plainTargetText = new DOMParser().parseFromString(targetText, 'text/html').body.textContent || '';
              } catch (error) {
                plainTargetText = targetText;
                console.error('Error removing HTML tags:', error);
              }

              // Check if no text is bookmarked
              if (plainTargetText.trim().length === 0) {
                return false;
              }

              return editor.commands.aiTextPrompt({
                text: `Rephrase the following text: ${plainTargetText}`,
                insertAt,
                ...options,
              })
            },
          aiCustomDefine:
            (options = {}) =>
            ({ editor }) => {
              const { from, to } = options.position;
              const targetText = getHTMLContentBetween(editor, from, to);
              const insertAt = { from, to };

              // Remove HTML tags from the text
              let plainTargetText = '';

              try {
                plainTargetText = new DOMParser().parseFromString(targetText, 'text/html').body.textContent || '';
              } catch (error) {
                plainTargetText = targetText;
                console.error('Error removing HTML tags:', error);
              }

              // Check if no text is bookmarked
              if (plainTargetText.trim().length === 0) {
                return false;
              }

              return editor.commands.aiTextPrompt({
                text: `Input: ${plainTargetText}. If a single word, return an object: {word, pronunciation, part_of_speech, definitions (list)}. If a sentence, return the first word of the sentence's definition in the same format. Return only the object, no extra text.`,
                insertAt,
                ...options,
              })
            }
        }
      }
    });

    const WordNode = Node.create({
      name: 'word',
      group: 'inline',
      inline: true,
      content: 'text*',

      addAttributes() {
        return {
          id: {
            default: null,
            parseHTML: element => element.getAttribute('id'),
            renderHTML: attributes => {
              if (!attributes.id) {
                return {}
              }
              return {
                id: attributes.id,
              }
            },
          }
        }
      },
      parseHTML() {
        return [
          {
            tag: 'span',
            preserveWhitespace: true,
          }
        ]
      },
      renderHTML({ HTMLAttributes }) {
        return ['span', HTMLAttributes, 0]
      },
    });

    const CustomExtension = Extension.create({
      name: "scrollableCurrentText",

      addGlobalAttributes() {
        return [
          {
            types: ["paragraph"],
            attributes: {
              scrollable: {
                default: null,
                parseHTML: element => element.getAttribute('data-scrollable'),
                renderHTML: attributes => {
                  if (!attributes.scrollable) {
                    return {}
                  }
                  return {
                    'data-scrollable': attributes.scrollable,
                    class: 'scrollable-paragraph'
                  }
                }
              }
            }
          },
          {
            types: ['word'],
            attributes: {
              scrollable: {
                default: null,
                parseHTML: element => element.getAttribute('data-scrollable'),
                renderHTML: attributes => {
                  if (!attributes.scrollable) {
                    return {}
                  }
                  return {
                    'data-scrollable': attributes.scrollable,
                    class: 'scrollable-word'
                  }
                }
              }
            }
          }
        ]
      },

      addCommands() {
        return {
          setCurrentScrollableParagraph: (id) => ({ tr, state }) => {
            // Remove current from all paragraphs
            state.doc.descendants((node, pos) => {
              if (node.type.name === 'paragraph' && node.attrs.scrollable) {
                tr.setNodeMarkup(pos, null, { ...node.attrs, scrollable: null })
              }
            })
        
            // Add current to target paragraph
            state.doc.descendants((node, pos) => {
              if (node.type.name === 'paragraph' && (node.attrs.id == id || node.attrs.id == `${id}-header`)) {
                tr.setNodeMarkup(pos, null, { ...node.attrs, scrollable: true })
              }
            })
        
            return tr.docChanged
          },
          setCurrentScrollableWord: (wordId) => ({ tr, state }) => {
            // Remove current word attribute from all spans
            state.doc.descendants((node, pos) => {
              if (node.type.name === 'word' && node.attrs.scrollable) {
                tr.setNodeMarkup(pos, null, { ...node.attrs, scrollable: null })
              }
            })
            
            // Add current word attribute to target span
            state.doc.descendants((node, pos) => {
              if (node.type.name === 'word' && node.attrs.id === wordId) {
                tr.setNodeMarkup(pos, null, { ...node.attrs, scrollable: true })
              }
            })
            
            return tr.docChanged
          },
          removeScrollableParagraph: () => ({ tr, state }) => {
            // Remove current from all paragraphs
            state.doc.descendants((node, pos) => {
              if (node.type.name === 'paragraph' && node.attrs.scrollable) {
                tr.setNodeMarkup(pos, null, { ...node.attrs, scrollable: null })
              }
            })
            return tr.docChanged
          },
          removeScrollableWord: () => ({ tr, state }) => {
            // Remove current word attribute from all spans
            state.doc.descendants((node, pos) => {
              if (node.type.name === 'word' && node.attrs.scrollable) {
                tr.setNodeMarkup(pos, null, { ...node.attrs, scrollable: null })
              }
            })
            return tr.docChanged
          },
        }
      }
    });
    
    // Timestamp Mark for Editor
    const TimestampMark = Mark.create({
      name: 'timestamp',

      addAttributes() {
        return {
          class: {
            default: null,
            parseHTML: element => element.getAttribute('class'),
            renderHTML: (attributes) => {
              if (!attributes.class) {
                return {};
              }
              return {
                class: attributes.class,
              };
            },
          },
        };
      },

      parseHTML() {
        return [
          {
            tag: 'span',
            getAttrs: element => {
              return element.classList.contains('timestamp') && null;
            },
          },
        ];
      },

      renderHTML({ HTMLAttributes }) {
        return ['span', HTMLAttributes, 0];
      },
    });
    
    const extensions = [
      Typography,
      Underline,
      Highlight.configure(
        {
          multicolor: true,
          HTMLAttributes: {
            class: 'text-editor-highlight',
          },
        }, 
      ),
      Subscript,
      Superscript,
      TextAlign,
      Color,
      TextStyle,
      Paragraph.extend({
        addAttributes() {
          return {
            id: {
              default: null, // change this if you want a different default value
              parseHTML: (element) => {
                // Get the attribute value from markup if it's available, otherwise assign null as the attribute value
                return element.hasAttribute("id") ? element.getAttribute("id") : null;
              },
            },
          }
        },
      }),
      aiExtension.configure({
        appId: `${process.env.VUE_APP_TIPTAP_ID}`,
        token: this.tiptapToken,
        onLoading: (value) => {
          this.$emit("openStudyAssistantModal", "loading", value);
        },
        onSuccess: (response) => {
          let plainTextResponse = response;
          plainTextResponse.response = new DOMParser().parseFromString(response.response, 'text/html').body.textContent;

          this.$emit("openStudyAssistantModal", "success", plainTextResponse);
        },
        onError: (error) => {
          this.$emit("openStudyAssistantModal", "error", error);
        }
      }),
      CustomExtension,
      WordNode,
      TimestampMark,
    ];

    this.setTranscriptEditor(
      new Editor({
        content: this.savedTranscript,
        extensions: [StarterKit, ...extensions],
      })
    );
  },

  methods: {},

  beforeUnmount() {
    this.transcriptEditor.destroy();
  },
};
</script>

<style lang="scss">
.transcript-editor {
  .text-editor {
    .text-editor-highlight {
      border-radius: 0;
      font-size: 1.8rem;
      padding: 0;
    }
    .ProseMirror {
      font-family: inherit;
      font-size: inherit;
      height: calc(100% - 3.6rem);

      mark {
        background-color: transparent;
        color: inherit;
      }

      .scrollable-paragraph {
        background-color: transparent !important;
        color: rgba(255, 255, 255, 1) !important;
      }

      .scrollable-word {
        background-color: map-get($colours, accent-purple-3) !important;
        border-radius: 8px;
        padding: 0.5rem;
      }
    }
  }
}
</style>
